/* Theme sync: 2026-09-03-base */
/**
 * JRS Unified Modal Controller
 * Handles all modals with consistent behavior
 * @version 2.0
 */
(function() {
  'use strict';
  
  // ===========================================
  // CONFIGURATION
  // ===========================================
  
  const CONFIG = {
    selectors: {
      trigger: '.jrs-modal-trigger',
      modal: '.jrs-modal',
      closeBtn: '[data-modal-close]',
      backdrop: '.jrs-modal__backdrop'
    },
    classes: {
      active: 'is-active',
      bodyLock: 'jrs-modal-active',
      inAppBrowser: 'is-in-app-browser'
    },
    dataAttributes: {
      target: 'data-modal-target'
    }
  };
  
  // ===========================================
  // STATE
  // ===========================================
  
  let activeModal = null;
  let previousFocus = null;
  let scrollPosition = 0;
  const modalOrigins = new WeakMap();
  
  // ===========================================
  // BROWSER DETECTION
  // ===========================================
  
  function detectBrowsers() {
    const ua = navigator.userAgent;
    const html = document.documentElement;
    
    // In-app browsers
    const inAppPatterns = ['FBAN', 'FBAV', 'Instagram', 'Twitter', 'LinkedInApp'];
    if (inAppPatterns.some(p => ua.includes(p))) {
      html.classList.add(CONFIG.classes.inAppBrowser);
    }
    
    // Specific browsers
    if (ua.includes('FBAN') || ua.includes('FBAV')) html.classList.add('is-facebook-browser');
    if (ua.includes('Instagram')) html.classList.add('is-instagram-browser');
    if (ua.includes('SamsungBrowser')) html.classList.add('is-samsung-browser');
    if (/iPad|iPhone|iPod/.test(ua)) html.classList.add('is-ios');
  }
  
  // ===========================================
  // VIEWPORT HEIGHT FIX
  // ===========================================
  
  function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
  
  // ===========================================
  // BODY SCROLL LOCK (iOS Compatible)
  // ===========================================
  
  function lockScroll() {
    scrollPosition = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.classList.add(CONFIG.classes.bodyLock);
  }
  
  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove(CONFIG.classes.bodyLock);
    window.scrollTo(0, scrollPosition);
  }
  
  // ===========================================
  // MODAL CONTROL
  // ===========================================

  /**
   * Move the modal to <body> so it escapes transformed/overflow ancestors
   * (e.g. product sections) and can stack above the customizer popup shell.
   */
  function ensureModalInBody(modal) {
    if (!modal || modal.parentElement === document.body) return;

    if (!modalOrigins.has(modal)) {
      modalOrigins.set(modal, {
        parent: modal.parentElement,
        nextSibling: modal.nextElementSibling
      });
    }

    document.body.appendChild(modal);
  }

  /** Popup shell owns body scroll lock via JRS.ScrollLock — do not clobber it. */
  function isExternalScrollLockActive() {
    return !!(window.JRS && JRS.ScrollLock &&
      typeof JRS.ScrollLock.isLocked === 'function' && JRS.ScrollLock.isLocked());
  }
  
  function openModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    ensureModalInBody(modal);
    
    // Close any open modal first
    if (activeModal) closeModal();
    
    // Store focus
    previousFocus = document.activeElement;
    
    // Lock scroll (skip when the customizer popup already holds the lock)
    if (!isExternalScrollLockActive()) {
      lockScroll();
    }
    
    // Show modal
    modal.classList.add(CONFIG.classes.active);
    modal.setAttribute('aria-hidden', 'false');
    activeModal = modal;
    
    // PDP GA4: guarantee and logo proof popup open
    if (window.jrsProductId && typeof window.jrsPDPTrack === 'function') {
      try {
        const triggerEl = previousFocus;
        const triggerLabel = (triggerEl && triggerEl.textContent) ? triggerEl.textContent.trim().slice(0, 80) : '';
        const deviceType = typeof window.jrsDeviceType === 'function' ? window.jrsDeviceType() : (window.innerWidth <= 768 ? 'mobile' : 'desktop');
        if (modalId === 'qualityGuaranteeModal') {
          window.jrsGuaranteeOpenTime = Date.now();
          window.jrsPDPTrack('pdp_guarantee_popup_open', {
            product_id: window.jrsProductId,
            trigger_element: triggerLabel,
            device_type: deviceType
          });
        } else if (modalId === 'logoProofsModal') {
          window.jrsLogoProofOpenTime = Date.now();
          window.jrsPDPTrack('pdp_logo_proof_popup_open', {
            product_id: window.jrsProductId,
            trigger_element: triggerLabel,
            device_type: deviceType
          });
        }
      } catch (e) { console.warn('[JRSUnifiedModal] PDP track open error', e); }
    }
    
    // Focus close button
    const closeBtn = modal.querySelector('.jrs-modal__close');
    if (closeBtn) closeBtn.focus();
    
    // Trap focus
    trapFocus(modal);
    
    // Android back button
    handleBackButton();
  }
  
  function closeModal() {
    if (!activeModal) return;
    
    // PDP GA4: guarantee and logo proof popup close (before clearing activeModal)
    if (window.jrsProductId && typeof window.jrsPDPTrack === 'function') {
      try {
        const deviceType = typeof window.jrsDeviceType === 'function' ? window.jrsDeviceType() : (window.innerWidth <= 768 ? 'mobile' : 'desktop');
        const modalId = activeModal.id;
        if (modalId === 'qualityGuaranteeModal' && window.jrsGuaranteeOpenTime) {
          const timeOpen = Math.round((Date.now() - window.jrsGuaranteeOpenTime) / 1000);
          window.jrsPDPTrack('pdp_guarantee_popup_close', {
            product_id: window.jrsProductId,
            time_open_seconds: timeOpen,
            device_type: deviceType
          });
        } else if (modalId === 'logoProofsModal' && window.jrsLogoProofOpenTime) {
          const timeOpen = Math.round((Date.now() - window.jrsLogoProofOpenTime) / 1000);
          window.jrsPDPTrack('pdp_logo_proof_popup_close', {
            product_id: window.jrsProductId,
            time_open_seconds: timeOpen,
            device_type: deviceType
          });
        }
      } catch (e) { console.warn('[JRSUnifiedModal] PDP track close error', e); }
    }
    
    // Hide modal
    activeModal.classList.remove(CONFIG.classes.active);
    activeModal.setAttribute('aria-hidden', 'true');
    
    // Unlock scroll (leave popup-owned lock intact)
    if (!isExternalScrollLockActive()) {
      unlockScroll();
    }
    
    // Restore focus
    if (previousFocus) previousFocus.focus();
    
    activeModal = null;
  }
  
  // ===========================================
  // FOCUS TRAP
  // ===========================================
  
  function trapFocus(element) {
    const focusable = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
    
    element.addEventListener('keydown', handleTab);
  }
  
  // ===========================================
  // ANDROID BACK BUTTON
  // ===========================================
  
  function handleBackButton() {
    history.pushState({ jrsModal: true }, '');
  }
  
  function onPopState(e) {
    if (activeModal) {
      closeModal();
      e.preventDefault();
    }
  }
  
  // ===========================================
  // EVENT HANDLERS
  // ===========================================
  
  function onTriggerClick(e) {
    const trigger = e.target.closest(CONFIG.selectors.trigger);
    if (!trigger) return;
    
    const targetId = trigger.getAttribute(CONFIG.dataAttributes.target);
    if (targetId) {
      e.preventDefault();
      openModal(targetId);
    }
  }
  
  function onTriggerKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      const trigger = e.target.closest(CONFIG.selectors.trigger);
      if (trigger) {
        e.preventDefault();
        const targetId = trigger.getAttribute(CONFIG.dataAttributes.target);
        if (targetId) openModal(targetId);
      }
    }
  }
  
  function onCloseClick(e) {
    if (e.target.closest(CONFIG.selectors.closeBtn)) {
      closeModal();
    }
  }
  
  function onEscape(e) {
    if (e.key === 'Escape' && activeModal) {
      closeModal();
    }
  }
  
  // ===========================================
  // INITIALIZATION
  // ===========================================
  
  function init() {
    // Detect browsers
    detectBrowsers();
    
    // Set viewport height
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', () => setTimeout(setViewportHeight, 150));
    
    // Event delegation for triggers
    document.addEventListener('click', onTriggerClick);
    document.addEventListener('keydown', onTriggerKeydown);
    
    // Close buttons
    document.addEventListener('click', onCloseClick);
    
    // Escape key
    document.addEventListener('keydown', onEscape);
    
    // Android back button
    window.addEventListener('popstate', onPopState);
  }
  
  // Run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

